import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface LoadingProps {
  /** Size of the loading spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Text to display alongside the spinner */
  text?: string;
  /** Render inline without centering */
  inline?: boolean;
  /** Render as fullscreen overlay */
  fullScreen?: boolean;
  /** Render as absolute overlay within container */
  overlay?: boolean;
  /** Color theme for the spinner */
  color?: 'primary' | 'white' | 'blue' | 'gray';
  /** Additional CSS classes */
  className?: string;
  /** Hide the spinner, only show text */
  hideSpinner?: boolean;
}

export interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Loading state */
  loading?: boolean;
  /** Text to show while loading */
  loadingText?: string;
  /** Icon to display before text */
  icon?: React.ReactNode;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
}

export interface LoadingOverlayProps {
  /** Text to display in overlay */
  text?: string;
  /** Blur the background */
  blur?: boolean;
  /** Custom z-index */
  zIndex?: number;
}

export interface ProgressBarProps {
  /** Progress percentage (0-100) */
  progress: number;
  /** Text label for the progress */
  text?: string;
  /** Show percentage value */
  showPercentage?: boolean;
  /** Color variant */
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'indigo';
  /** Height size */
  size?: 'sm' | 'md' | 'lg';
  /** Animated stripes */
  animated?: boolean;
}

// ============================================================================
// Main Loading Component
// ============================================================================

export const Loading: React.FC<LoadingProps> = ({
  size = 'md',
  text,
  inline = false,
  fullScreen = false,
  overlay = false,
  color = 'primary',
  className,
  hideSpinner = false,
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-2',
    lg: 'h-12 w-12 border-4',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const colorClasses = {
    primary: 'border-indigo-600 border-t-transparent',
    white: 'border-white border-t-transparent',
    blue: 'border-blue-600 border-t-transparent',
    gray: 'border-gray-600 border-t-transparent',
  };

  const containerClasses = cn(
    'flex items-center',
    {
      'justify-center': !inline,
      'fixed inset-0 bg-white bg-opacity-90 z-50': fullScreen,
      'absolute inset-0 bg-black bg-opacity-50 z-10 rounded-lg': overlay,
    },
    className
  );

  const spinnerClasses = cn(
    'animate-spin rounded-full',
    sizeClasses[size],
    overlay || fullScreen ? 'border-white border-t-transparent' : colorClasses[color]
  );

  const textClasses = cn(
    'ml-3',
    textSizeClasses[size],
    overlay || fullScreen ? 'text-white' : 'text-gray-600'
  );

  return (
    <div
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-label={text || 'Loading'}
      data-testid="loading-container"
    >
      {!hideSpinner && (
        <div
          className={spinnerClasses}
          data-testid="loading-spinner"
        />
      )}
      {text && (
        <span className={textClasses}>
          {text}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// LoadingButton Component
// ============================================================================

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  loadingText = 'Processing...',
  children,
  disabled,
  icon,
  variant = 'primary',
  size = 'md',
  className,
  ...props
}) => {
  const baseClasses = 'relative inline-flex items-center justify-center transition-colors duration-200 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
    ghost: 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const buttonClasses = cn(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className
  );

  return (
    <button
      disabled={disabled || loading}
      className={buttonClasses}
      {...props}
    >
      {loading ? (
        <>
          <Loading size="sm" inline hideSpinner={false} />
          <span className="ml-2">{loadingText}</span>
        </>
      ) : (
        <>
          {icon && <span className="mr-2">{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

// ============================================================================
// LoadingOverlay Component
// ============================================================================

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ 
  text,
  blur = false,
  zIndex = 10,
}) => {
  const { t } = useTranslation();
  const defaultText = text || t('common.loading');
  
  const overlayClasses = cn(
    'absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg',
    {
      'backdrop-blur-sm': blur,
    }
  );

  return (
    <div 
      className={overlayClasses}
      style={{ zIndex }}
      data-testid="loading-overlay"
    >
      <Loading text={defaultText} />
    </div>
  );
};

// ============================================================================
// ProgressBar Component
// ============================================================================

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  text,
  showPercentage = true,
  color = 'blue',
  size = 'md',
  animated = false,
}) => {
  const colorClasses = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    yellow: 'bg-yellow-600',
    red: 'bg-red-600',
    indigo: 'bg-indigo-600',
  };

  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  const clampedProgress = Math.min(100, Math.max(0, progress));
  const roundedProgress = Math.round(clampedProgress);

  const progressBarClasses = cn(
    'transition-[width] duration-300 ease-out',
    colorClasses[color],
    sizeClasses[size],
    {
      'bg-gradient-to-r from-current to-transparent animate-pulse': animated,
    }
  );

  return (
    <div className="w-full">
      {(text || showPercentage) && (
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          {text && <span>{text}</span>}
          {showPercentage && <span>{roundedProgress}%</span>}
        </div>
      )}
      <div className={cn('w-full bg-gray-200 rounded-full overflow-hidden', sizeClasses[size])}>
        <div
          className={progressBarClasses}
          style={{ width: `${clampedProgress}%` }}
          data-testid="progress-bar"
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Spinner Component (Inline SVG variant for backward compatibility)
// ============================================================================

export const Spinner: React.FC<{ 
  size?: 'sm' | 'md' | 'lg'; 
  className?: string;
  color?: string;
}> = ({
  size = 'md',
  className = '',
  color = 'currentColor',
}) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-6 w-6',
  };

  return (
    <svg
      className={cn('animate-spin', sizeClasses[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      data-testid="svg-spinner"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke={color}
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill={color}
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

// ============================================================================
// Default Export
// ============================================================================

export default Loading;